import React, { useMemo } from "react";
import ArrowDownwardIcon from "@mui/icons-material/ArrowDownward";
import ArrowUpwardIcon from "@mui/icons-material/ArrowUpward";
import { FormatMoney } from "../../../Helpers/FormatNumber";
import { CANTIDAD_MOSTRAR_OPCIONES } from "../../../Arrays/CantidadMostrarArray";
import { ordenarYLimitarPorAnio, MAX_FILAS_RANKING } from "../../../Helpers/RankingUtil";

const METRICAS = [
    { valor: "monto", etiqueta: "Montos" },
    // { valor: "clientes", etiqueta: "Clientes" },
    { valor: "cantidad", etiqueta: "Unidades" },
];

const ETIQUETA_METRICA = { monto: "venta", clientes: "Clientes", cantidad: "cantidad" };

const formatValor = (valor, metrica) => {
    if (!valor) return "—";
    if (metrica === "clientes") return Math.round(valor).toLocaleString("es-MX");
    if (metrica === "cantidad") return `${Math.round(valor).toLocaleString("es-MX")} unidades`;
    return `$${FormatMoney(valor, 2)}`;
};

// "Familias del grupo 80%/20%" (Pantalla 1): detalle de Familias del grupo
// activo en TablaParetoPorAnio ("80"/"20"/"total"), 2 columnas por año
// (valor + % del total del grupo ese año) + fila "Total" al final (suma de
// TODAS las Familias del grupo por año, sin importar el límite de
// `cantidad`). Detalle completo en README.
export const TablaFamiliasPorGrupoPareto = ({
    datos = [],
    anios = [],
    anioPrincipal,
    grupo = "80",
    metrica = "monto",
    onMetricaChange,
    cantidad = "10",
    onCantidadChange,
    orden = "desc",
    onOrdenChange,
}) => {
    const { seleccionado, limitadoPorTope, ascendente } = ordenarYLimitarPorAnio(datos, {
        anioClave: anioPrincipal,
        orden,
        cantidad,
    });

    // Invierte para "desc" (el helper siempre regresa orden ascendente).
    const filasVisibles = orden === "asc" ? seleccionado : [...seleccionado].reverse();

    // Total por año de TODAS las Familias del grupo (`datos` completo, no
    // `filasVisibles`, para que no cambie al limitar/ordenar): alimenta la
    // fila "Total" y el % que muestra cada celda (valor de la Familia ÷
    // total del grupo ese año). Precalculado una sola vez por año (no por
    // celda) para no repetir la suma sobre `datos` cientos de veces.
    const totalesPorAnio = useMemo(() => {
        const mapa = {};
        anios.forEach((anio) => {
            mapa[anio] = datos.reduce((suma, fila) => suma + (fila.valores[anio] || 0), 0);
        });
        return mapa;
    }, [datos, anios]);

    const tituloGrupo = grupo === "total" ? "Familias (Total de Clientes)" : `Familias del grupo ${grupo}%`;

    return (
        <div className="tabla-familias-grupo-pareto">
            <div className="tfgp-header">
                <div className="tfgp-titulo">{tituloGrupo}</div>
                <div className="tfgp-controles">
                    <div className="tfgp-metrica-toggle">
                        {METRICAS.map((opcion) => (
                            <button
                                key={opcion.valor}
                                type="button"
                                className={`tfgp-metrica-boton${metrica === opcion.valor ? " tfgp-metrica-boton--activo" : ""
                                    }`}
                                onClick={() => onMetricaChange && onMetricaChange(opcion.valor)}
                            >
                                {opcion.etiqueta}
                            </button>
                        ))}
                    </div>
                    <select
                        className="tfgp-cantidad"
                        value={cantidad}
                        onChange={({ target }) => onCantidadChange && onCantidadChange(target.value)}
                    >
                        {CANTIDAD_MOSTRAR_OPCIONES.map((opcion) => (
                            <option key={opcion} value={opcion}>
                                {opcion === "todos" ? "Todos" : opcion}
                            </option>
                        ))}
                    </select>
                    <div
                        className="tfgp-orden"
                        title={
                            orden === "desc"
                                ? "Mostrando de mayor a menor. Clic para invertir."
                                : "Mostrando de menor a mayor. Clic para invertir."
                        }
                        onClick={() => onOrdenChange && onOrdenChange(orden === "desc" ? "asc" : "desc")}
                    >
                        {orden === "desc" ? <ArrowDownwardIcon /> : <ArrowUpwardIcon />}
                    </div>
                </div>
            </div>

            {limitadoPorTope && (
                <div className="tfgp-aviso-limite">
                    Mostrando las {MAX_FILAS_RANKING} con mayor {ETIQUETA_METRICA[metrica]} (de {ascendente.length}{" "}
                    en total).
                </div>
            )}

            <div className="tfgp-scroll scroll">
                <table className="tfgp-tabla">
                    <thead>
                        <tr>
                            <th className="th-familia">Familia</th>
                            {anios.map((anio) => (
                                <React.Fragment key={anio}>
                                    <th>{anio}</th>
                                    <th className="tfgp-col-porcentaje" title={`% del total del grupo ${grupo === "total" ? "" : grupo + "%"} ese año`}>
                                        %
                                    </th>
                                </React.Fragment>
                            ))}
                        </tr>
                    </thead>
                    <tbody>
                        {filasVisibles.length === 0 && (
                            <tr>
                                <td className="tfgp-vacio" colSpan={anios.length * 2 + 1}>
                                    Sin datos para este grupo.
                                </td>
                            </tr>
                        )}
                        {filasVisibles.map((fila) => (
                            <tr key={fila.id}>
                                <td className="tfgp-col-familia" title={fila.etiqueta}>
                                    {fila.etiqueta}
                                </td>
                                {anios.map((anio) => {
                                    const valor = fila.valores[anio];
                                    const totalAnio = totalesPorAnio[anio];
                                    // % que representa esta Familia del total del grupo ESE año
                                    // (no del total general): ej. 100,000 de un grupo "80" con
                                    // 1,000,000 ese año = 10%. Sin valor o sin total, no se pinta.
                                    const porcentaje = valor && totalAnio > 0 ? (valor / totalAnio) * 100 : null;
                                    return (
                                        <React.Fragment key={anio}>
                                            <td>{formatValor(valor, metrica)}</td>
                                            <td className="tfgp-col-porcentaje">
                                                {porcentaje !== null ? `${porcentaje.toFixed(2)}%` : "—"}
                                            </td>
                                        </React.Fragment>
                                    );
                                })}
                            </tr>
                        ))}
                    </tbody>
                    {filasVisibles.length > 0 && (
                        <tfoot>
                            <tr className="tfgp-fila-total">
                                <td className="tfgp-col-familia">Total</td>
                                {anios.map((anio) => (
                                    <React.Fragment key={anio}>
                                        <td>{formatValor(totalesPorAnio[anio], metrica)}</td>
                                        <td className="tfgp-col-porcentaje">
                                            {totalesPorAnio[anio] ? "100.0%" : "—"}
                                        </td>
                                    </React.Fragment>
                                ))}
                            </tr>
                        </tfoot>
                    )}
                </table>
            </div>
        </div>
    );
};
